import type { RFileUploadResponse } from 'v2/types/api/auth'
import type { ConvChannels } from 'v2/types/api/conversations'
import type { FlowNodeTypes } from '../types'

/**
 * Flow Run Info Types
 *
 * This file contains type definitions for flow execution events and node configurations.
 *
 * PROMPT TYPES USAGE EXAMPLES:
 *
 * // Using type guards to safely handle different prompt types:
 * const handlePrompt = (prompt: unknown) => {
 *   if (isStringPrompt(prompt)) {
 *     console.log('Simple string prompt:', prompt)
 *   } else if (isBasePromptConfig(prompt)) {
 *     console.log('Audio prompt:', prompt.text, 'Type:', prompt.prompt_type)
 *   } else if (isSimpleTextPrompt(prompt)) {
 *     console.log('Text prompt object:', prompt.text)
 *   }
 * }
 *
 * // Using the utility function to extract text from any prompt type:
 * const promptText = getPromptText(node.node_config?.prompt)
 *
 * // Type-safe access to prompt properties:
 * if (isPromptWithLoops(prompt)) {
 *   console.log('This prompt repeats', prompt.loops, 'times')
 * }
 */

/**
 * Chat and SMS rows share the same transcript/tool/card payload shapes, so the
 * run page renders them identically. Spelled once here — widening a channel
 * gate means using this, never a second inline `=== 'chat'`.
 */
export const isTextAgentChannel = (channel?: string | null): boolean =>
  channel === 'chat' || channel === 'message'

// Base interfaces for common properties
interface BaseFlowEvent {
  flow_run_uuid: string
  node_uuid: string
  node_run_uuid: string
  auth_id: string
  timestamp: string
}

// Different prompt types based on their structure and usage
interface BasePromptConfig {
  delay: number
  file: string
  language: string
  prompt_speak_type: string
  prompt_type: 'speak' | 'play'
  text: string
  url: string
  voice: string
}

interface PromptWithLoopsConfig extends BasePromptConfig {
  loops: number
}

// Simple text prompt (used by AI agents)
interface SimpleTextPrompt {
  text: string
  [key: string]: unknown
}

// Union type for all prompt configurations
type PromptConfig = BasePromptConfig | PromptWithLoopsConfig | SimpleTextPrompt

// Type guards for prompt types
const isBasePromptConfig = (prompt: unknown): prompt is BasePromptConfig =>
  typeof prompt === 'object' &&
  prompt !== null &&
  'prompt_type' in prompt &&
  'delay' in prompt &&
  'language' in prompt

const isPromptWithLoops = (prompt: unknown): prompt is PromptWithLoopsConfig =>
  isBasePromptConfig(prompt) && 'loops' in prompt

const isSimpleTextPrompt = (prompt: unknown): prompt is SimpleTextPrompt =>
  typeof prompt === 'object' && prompt !== null && 'text' in prompt && !('prompt_type' in prompt)

const isStringPrompt = (prompt: unknown): prompt is string => typeof prompt === 'string'

// Helper function to extract text from any prompt type
const getPromptText = (prompt: unknown, depth = 0): string | undefined => {
  const MAX_DEPTH = 10 // Prevent stack overflow from deeply nested arrays

  if (depth > MAX_DEPTH) {
    return undefined
  }

  if (isStringPrompt(prompt)) {
    return prompt
  }
  if (isBasePromptConfig(prompt) || isSimpleTextPrompt(prompt)) {
    return prompt.text
  }
  if (Array.isArray(prompt)) {
    return prompt
      .map((p) => getPromptText(p, depth + 1))
      .filter(Boolean)
      .join(', ')
  }
  return undefined
}

interface DueInConfig {
  value: number
  unit: string
}

// New content type for chat messages
interface ChatMessageContent {
  is_first_message: boolean
  message: string | RFileUploadResponse[]
  sender: 'customer'
}

// Content for WhatsApp/SMS messages
interface StandardMessageContent {
  auth_id: string
  message_uuid: string
  from: string
  to: string
  body?: string
  text?: string
  type: string
  content_type: 'text' | 'media'
  phlo_id: string
  aom_uuid: string
  conv_channel: string
  // File-related fields for media messages (WhatsApp only)
  mime_type?: string // MIME type of the media file
  file_attributes?: {
    file_url: string // URL of the uploaded file
    file_type: string // Type/format of the file
    file_id: string // Unique identifier for the file
    file_name: string // Original name of the file
    file_size: number // Size of the file in bytes
  }
}

// Generic async message payload that can handle any channel type
export interface AsyncMessagePayload {
  content: StandardMessageContent | ChatMessageContent
  type: 'message' | 'file'
}

// Simplified single node configuration interface
// Contains only properties actually used in analysis components
interface NodeConfig {
  // AI Agent nodes (ai-nodes.tsx)
  prompt?: string | PromptConfig | PromptConfig[]
  variables?:
    | Record<string, unknown>
    | Record<
        string,
        {
          variable_value: string
          variable_type: 'string' | 'number' | 'boolean' | 'array' | 'object'
        }
      >
    | Array<{
        variable_name: string
        variable_value: string
        variable_type: 'string' | 'number' | 'boolean' | 'array' | 'object'
      }>
  conversation_type?: 'ai_generated' | 'human_generated' | 'preset_message'
  greeting_type?: 'GENERATED' | 'CUSTOM'
  greeting_prompt?: string
  completion_criteria?: string
  human_handoff_prompt?: string
  model_config?: { model: string; provider: string }

  // Per-node execution duration in milliseconds (emitted by the backend for all nodes)
  execution_time?: number

  // Tool call nodes (tool-call.tsx)
  tool_response?: string
  execution_status?: string
  tool_time_taken?: number

  // Skill nodes (skill-node.tsx)
  skill_input?: Record<string, unknown>
  skill_result?: string
  skill_type?: string
  skill_status?: string
  time_taken?: number

  // IVR Menu nodes (non-ai-nodes.tsx)
  configured_inputs?: string

  // Various prompt-based nodes (non-ai-nodes.tsx)
  prompts?: PromptConfig | PromptConfig[]

  // Call nodes
  from?: string
  to?: string
  concurrency_limit_applied?: boolean

  // SMS nodes
  text?: string

  // Task creation nodes
  task_type?: string
  // Also the executed node's own channel ('chat', 'call', …) on a run log, which
  // is what gates channel-specific rendering in helpers shared across channels.
  channel?: string
  priority?: string
  due_in?: DueInConfig

  // Queue and routing nodes
  queue_priority?: number
  assignment_status?: string
  assigned_to?: string

  // HTTP request nodes
  response?: string
  status_code?: number

  // User input nodes
  validation?: string

  // Collect response nodes
  response_recieved?: string

  // Generic properties for extensibility
  [key: string]: unknown
  // Attributes can be any type, so we need to use any
  // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
  attributes?: Record<string, any>
}

// Main event interfaces
interface NodeExecutedEvent extends BaseFlowEvent {
  event_name: 'NODE_EXECUTED'
  current_node: string
  current_node_uuid: string
  current_node_type: FlowNodeTypes | null
  chosen_path: string
  previous_node: string
  previous_node_uuid: string
  previous_node_type?: FlowNodeTypes
  node_config?: NodeConfig & Record<string, string | null>
  chunks?: Array<NodeChunkCreatedEvent | SystemChunkCreatedEvent>
  aggregated_variables?: NodeConfig['variables']
  isBaseNode?: boolean // Flag to identify base nodes created from orphaned chunks
}

// Metrics interfaces
interface LLMMetrics {
  time: number
  total_tokens: number
  provider_name: string
  model_name: string
}
export interface KBChunk {
  content: string
}
interface KBReference {
  chunks: Array<KBChunk>
  time: number
}
interface KBMetrics {
  references: Array<KBReference>
}

interface ChunkMetrics {
  llm_metrics: LLMMetrics | null
  tool_metrics: Array<{
    tool_name: string
    tool_response: string
    tool_input: Record<string, unknown>
    time: number
    // always set by the agent runtime; absent on older rows
    tool_call_error?: boolean
  }> | null
  kb_metrics: KBMetrics | null
}

export interface MessageAttributes {
  header: { type: string; text: string; media: unknown | null }
  body: string
  footer: unknown | null
  buttons: Array<{ title: string; id: string }>
}

export interface FileMessageAttributes {
  file_count: number
  files: Array<RFileUploadResponse>
}

// New type for chat menu button content
export interface ChatMenuButtons {
  sender: 'bot'
  message: Array<{
    description: string
    title: string
    buttons: Array<{
      button_type: 'reply' | 'call' | 'website'
      text: string
      id: string
      call_country: string | null
      call_phone: string | null
      website_url: string | null
    }>
  }>
  messageTimestamp: string
}

export interface ChatTextMessage {
  sender: 'bot'
  message: string
  messageTimestamp: string
}

// New type for chat multi-product content
export interface ChatMultiProduct {
  sender: 'bot'
  message: Array<{
    items: Array<{
      header: { name: string; media_type: string | null; url: string; size: number }
      description: string
      id: string
      buttons: Array<{
        button_type: 'reply' | 'call' | 'website'
        text: string
        id: string
        call_country: string | null
        call_phone: string | null
        website_url: string | null
      }>
      title: string
    }>
  }>
  messageTimestamp: string
  carousel_message: string
}

export interface ChatMessageAttributes {
  type: string
  content: unknown | null | ChatMenuButtons | ChatMultiProduct | ChatTextMessage
}

interface NodeChunkCreatedEvent extends BaseFlowEvent {
  event_name: 'NODE_CHUNK_CREATED'
  type: 'text'
  // `agent` is a human agent on a chat handoff — distinct from `assistant`, the AI, which the
  // header counts as an AI response.
  sender: 'assistant' | 'user' | 'system' | 'agent'
  // Who wrote an `agent` line. Only the human leg carries one.
  sender_name?: string
  text: string
  message?: string
  attributes: MessageAttributes[] | FileMessageAttributes | ChatMessageAttributes | null
  metrics: ChunkMetrics | null
  node_config: Partial<NodeConfig>
}

type SystemChunkType =
  | 'USER_STARTED_SPEAKING'
  | 'USER_STOPPED_SPEAKING'
  | 'BOT_STARTED_SPEAKING'
  | 'BOT_STOPPED_SPEAKING'
  | 'MODEL_STARTED_EVENT'
  | 'TOOL_CALL_EVENT'
  | 'USER_MIGHT_STILL_BE_SPEAKING'
  | 'SKILL_NODE_EXECUTED'
  | 'KNOWLEDGE_BASE_SEARCH_EVENT'
  | 'SPEECH_DETECTED'
  | 'FILLER_DROPPED'
  | 'TURN_DETECTION_METRIC'
  | 'VARIABLE_SKIPPED'
  | 'TASK_STARTED'
  // A human agent arriving on / leaving a chat handoff. Chat only.
  | 'AGENT_ASSIGNED'
  | 'AGENT_LEFT'
  | 'AGENT_RESPONSE' // UI only, not generated in the backend
  | 'USER_RESPONSE' // UI only, not generated in the backend

// Detail payload of TASK_STARTED ({task, label}) and VARIABLE_SKIPPED
// ({variable, kind, reason}) system chunks.
interface SystemChunkMetadata {
  task?: string
  label?: string
  variable?: string
  kind?: string
  reason?: string
}

interface SystemChunkCreatedEvent extends BaseFlowEvent {
  event_name: 'SYSTEM_CHUNK_CREATED'
  type: SystemChunkType
  text: string
  sender: 'system'
  message: string
  metadata?: SystemChunkMetadata | null
  // Tool-event detail (TOOL_CALL_EVENT / KNOWLEDGE_BASE_SEARCH_EVENT). Optional
  // so non-tool system events are unaffected.
  tool_name?: string
  tool_input?: Record<string, unknown> | null
  tool_response?: string | null
  tool_call_error?: boolean
  time?: number | null
}

interface FlowExecutionCompleteEvent extends Omit<NodeExecutedEvent, 'event_name'> {
  event_name: 'FLOW_EXECUTION_COMPLETE'
  message: string
}

interface NodeUpdatedEvent extends BaseFlowEvent {
  event_name: 'NODE_UPDATED'
  node_config: Partial<NodeConfig>
}

export interface Metrics {
  EOU_LLM_latency: number
  EOU_transcript_latency: number
  llm_total_time: number
  llm_ttfb_time: number
  node_transition_time: number
  pipeline_latency: number
  stt_latency_precomputed: boolean
  stt_processing_time: number
  tts_ttfb_time: number
  turn_detector_time: number
}

interface IntermediateMetric {
  EOU_LLM_latency: number
  eou_transcript_latency: number
  llm_text: string
  llm_ttfb_time: number
  stt_processing_time: number
  transcription_text: number
  turn_detector_time: number
}

interface MetricsChunkCreatedEvent extends BaseFlowEvent {
  event_name: 'METRICS_CHUNK_CREATED'
  input_text: string
  intermediate_metrics?: IntermediateMetric[]
  message: string
  metrics: Metrics
  output_text: string
  source: string
}

// Union type for all events
export type FlowRunEvent =
  | NodeExecutedEvent
  | NodeUpdatedEvent
  | NodeChunkCreatedEvent
  | SystemChunkCreatedEvent
  | FlowExecutionCompleteEvent
  | MetricsChunkCreatedEvent

// Export individual interfaces for specific use cases
export type {
  BaseFlowEvent,
  NodeConfig,
  NodeExecutedEvent,
  NodeUpdatedEvent,
  NodeChunkCreatedEvent,
  SystemChunkCreatedEvent,
  FlowExecutionCompleteEvent,
  SystemChunkMetadata,
  SystemChunkType,
  // Prompt types
  PromptConfig,
  BasePromptConfig,
  PromptWithLoopsConfig,
  SimpleTextPrompt,
  // Utility types
  DueInConfig,
  LLMMetrics,
  ChunkMetrics,
  MetricsChunkCreatedEvent,
}

// Type guard for ChatMenuButtons
export const isChatMenuButtons = (content: unknown): content is ChatMenuButtons =>
  typeof content === 'object' &&
  content !== null &&
  'sender' in content &&
  'message' in content &&
  'messageTimestamp' in content &&
  (content as ChatMenuButtons).sender === 'bot' &&
  Array.isArray((content as ChatMenuButtons).message)

// Type guard for ChatMultiProduct
export const isChatMultiProduct = (content: unknown): content is ChatMultiProduct =>
  typeof content === 'object' &&
  content !== null &&
  'sender' in content &&
  'message' in content &&
  'messageTimestamp' in content &&
  'carousel_message' in content &&
  (content as ChatMultiProduct).sender === 'bot' &&
  Array.isArray((content as ChatMultiProduct).message) &&
  Array.isArray((content as ChatMultiProduct).message[0]?.items)

// Type guard for FileMessageAttributes
export const isFileMessageAttributes = (attributes: unknown): attributes is FileMessageAttributes =>
  typeof attributes === 'object' &&
  attributes !== null &&
  !Array.isArray(attributes) &&
  'files' in attributes &&
  Array.isArray((attributes as FileMessageAttributes).files)

// Type guard for ChatMessageAttributes
export const isChatMessageAttributes = (attributes: unknown): attributes is ChatMessageAttributes =>
  typeof attributes === 'object' &&
  attributes !== null &&
  !Array.isArray(attributes) &&
  'type' in attributes &&
  'content' in attributes

// Type guard for MessageAttributes array
export const isMessageAttributesArray = (attributes: unknown): attributes is MessageAttributes[] =>
  Array.isArray(attributes)

// Type guard for ChatTextMessage
export const isChatTextMessage = (content: unknown): content is ChatTextMessage =>
  typeof content === 'object' &&
  content !== null &&
  'sender' in content &&
  'message' in content &&
  'messageTimestamp' in content &&
  (content as ChatTextMessage).sender === 'bot' &&
  typeof (content as ChatTextMessage).message === 'string'

// Export type guards and utility functions
export { isBasePromptConfig, isPromptWithLoops, isSimpleTextPrompt, isStringPrompt, getPromptText }

// Helper function for creating async message payloads (supports both text and media)
export const createAsyncMessagePayload = (
  basePayload: {
    authId: string
    messageUUID: string
    from: string
    to: string
    body?: string
    text?: string
    phloId: string
    aomUUID: string
    // Optional file attributes for media messages (WhatsApp only)
    fileAttributes?: {
      file_url: string
      file_type: string
      file_id: string
      file_name: string
      file_size: number
    }
    mimeType?: string // Required when fileAttributes is provided
    isFirstMessage?: boolean
  },
  channelType: ConvChannels,
): AsyncMessagePayload => {
  if (channelType === 'chat') {
    const isFile = !!basePayload.fileAttributes
    return {
      type: isFile ? 'file' : 'message',
      content: {
        is_first_message: !!basePayload.isFirstMessage,
        message: isFile
          ? [basePayload.fileAttributes as RFileUploadResponse]
          : basePayload.text || '',
        sender: 'customer',
      },
    }
  }

  return {
    type: 'message',
    content: {
      auth_id: basePayload.authId,
      message_uuid: basePayload.messageUUID,
      from: basePayload.from,
      to: basePayload.to,
      body: basePayload.body || '',
      text: basePayload.text || '',
      type: channelType === 'whatsapp' ? 'whatsapp' : 'sms',
      content_type: basePayload.fileAttributes ? 'media' : 'text',
      phlo_id: basePayload.phloId,
      aom_uuid: basePayload.aomUUID,
      conv_channel: channelType || '',
      // Include file-related fields only when fileAttributes is provided
      ...(basePayload.fileAttributes && {
        mime_type: basePayload.mimeType,
        file_attributes: basePayload.fileAttributes,
      }),
    },
  }
}

// Type guard to check if channel supports async messaging
const ASYNC_MESSAGING_CHANNELS = ['whatsapp', 'message', 'chat', 'slack'] as const
type AsyncMessagingChannel = (typeof ASYNC_MESSAGING_CHANNELS)[number]

export const isAsyncMessagingChannel = (channel: string): channel is AsyncMessagingChannel =>
  ASYNC_MESSAGING_CHANNELS.includes(channel as AsyncMessagingChannel)
